import { supabase } from './supabase';
import { fetchSignatariosDefault } from './termoSignatarios';
import { getTermoValido } from './termoValido';

export const TERMO_LIBERAR_ASSINATURAS_KEY = 'termo_liberar_assinaturas_webhook_url';

/**
 * Pós-aprovação da análise cadastral: adiciona procurador + testemunhas como
 * signatários do documento já existente no Autentique (via webhook n8n que
 * executa as mutations createSigner). O cliente assinou na emissão; os
 * signatários internos só entram depois da aprovação.
 *
 * Best-effort: falhas não travam a finalização da análise — registram nota na
 * negociação para liberação manual.
 *
 * @returns 'ok' | 'sem_termo' | 'erro'
 */
export async function liberarAssinaturasInternas(negociacaoId: number | string): Promise<'ok' | 'sem_termo' | 'erro'> {
    try {
        const { data: neg, error } = await supabase
            .from('negociacoes')
            .select('url_termo')
            .eq('id', negociacaoId)
            .single();
        if (error) throw error;

        const termos: any[] = neg?.url_termo || [];
        // Mesma regra da tela e da reconciliação: o termo operante é o ÚLTIMO
        // ASSINADO. A regra antiga ("o `validado`, senão o último do array") mandou
        // procurador e testemunhas para o documento errado na negociação 3856 — o
        // termo que o cliente realmente assinou ficou sem os signatários internos e
        // nunca seria concluído no Autentique.
        const candidatos = termos.filter(t => t?.id_autentique);
        const termo = getTermoValido(candidatos);

        if (!termo) {
            await registrarNotaLiberacao(negociacaoId,
                '[ASSINATURAS INTERNAS] Termo aprovado, mas nenhum termo desta negociação tem o ID do Autentique salvo ' +
                '(termo emitido antes da atualização do sistema). Adicione procurador e testemunhas manualmente no Autentique.');
            return 'sem_termo';
        }

        if (termo.assinaturas_liberadas_em) return 'ok'; // já liberado (reanálise)

        const { data: settings } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', TERMO_LIBERAR_ASSINATURAS_KEY)
            .single();
        const webhookUrl = settings?.value || '';
        if (!webhookUrl) {
            await registrarNotaLiberacao(negociacaoId,
                '[ASSINATURAS INTERNAS] Webhook de liberação de assinaturas não configurado em Config > APIs ' +
                `(chave ${TERMO_LIBERAR_ASSINATURAS_KEY}). Adicione procurador e testemunhas manualmente no Autentique.`);
            return 'erro';
        }

        // Signatários escolhidos na emissão; fallback: config atual
        let sig = termo.signatarios || {};
        if (!sig.procurador || !sig.testemunha1 || !sig.testemunha2) {
            const defaults = await fetchSignatariosDefault();
            sig = {
                procurador: sig.procurador || defaults.procurador,
                testemunha1: sig.testemunha1 || defaults.testemunha1,
                testemunha2: sig.testemunha2 || defaults.testemunha2
            };
        }

        const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                negociacao_id: negociacaoId,
                id_autentique: termo.id_autentique,
                procurador: sig.procurador,
                testemunha1: sig.testemunha1,
                testemunha2: sig.testemunha2
            })
        });
        if (!response.ok) throw new Error(`Webhook retornou ${response.status}`);
        // Se o fluxo falha antes do respond, o n8n devolve 200 com corpo vazio — exige ok:true explícito
        let parsed: any = null;
        try { parsed = JSON.parse(await response.text()); } catch { /* corpo vazio */ }
        if (!parsed || parsed.ok !== true) {
            throw new Error('O n8n não confirmou a adição dos signatários (verifique a execução do fluxo "Liberar assinaturas internas").');
        }

        // Marca o termo como liberado (regrava o array com o item atualizado)
        const atualizados = termos.map(t =>
            t === termo ? { ...t, assinaturas_liberadas_em: new Date().toISOString() } : t
        );
        await supabase.from('negociacoes').update({ url_termo: atualizados }).eq('id', negociacaoId);

        return 'ok';
    } catch (err: any) {
        console.error('Erro ao liberar assinaturas internas:', err);
        await registrarNotaLiberacao(negociacaoId,
            `[ASSINATURAS INTERNAS] Falha ao liberar assinaturas do procurador/testemunhas no Autentique: ${err?.message || 'erro desconhecido'}. ` +
            'Adicione os signatários manualmente no Autentique ou finalize a análise novamente.');
        return 'erro';
    }
}

async function registrarNotaLiberacao(negociacaoId: number | string, conteudo: string) {
    try {
        const { data: userData } = await supabase.auth.getUser();
        await supabase.from('negociacao_notas').insert({
            negociacao_id: negociacaoId,
            conteudo,
            tipo: 'nota',
            user_id: userData?.user?.id || null
        });
    } catch (e) {
        console.error('Erro ao registrar nota de liberação:', e);
    }
}
